/**
 * @file ResetPasswordForm.js
 * @description Represents the reset password page component with enhanced password validation and token verification.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../../../services/authService';
import { useTheme } from '../../../store/ThemeContext';
import PasswordInput from '../../ui/PasswordInput';
import styles from '../AuthPage.module.scss';
import { hasSequentialChars } from '../../../utils/utils';
import PlaceholderLogo from '../../ui/PlaceholderLogo';
import { ROUTE_BUILDERS } from '../../../constants/routes';
import { getText } from '../../../i18n';
function ResetPasswordForm({ onSuccess, handleAuthError, authData }) {
    const themeStyles = useTheme();
    const { companyCode, companyConfig } = authData;
    const { id: resetId } = useParams();
    const navigate = useNavigate();

    const [isVerifying, setIsVerifying] = useState(true);

    const [resetFormData, setResetFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const passwordRef = useRef(null);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
    });

    useEffect(() => {
        passwordRef.current?.focus();
    }, []);

    // Verify token on mount
    useEffect(() => {
        const verifyToken = async () => {
            setIsVerifying(true);
            try {
                await resetPassword(resetId, '', true);
            } catch (error) {
                navigate(ROUTE_BUILDERS.forgotPasswordResetExpired(companyCode), { replace: true });
            } finally {
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, [resetId, companyCode, navigate]);

    const handleSubmit = async event => {
        event.preventDefault();
        const newErrors = validateForm(resetFormData);

        if (Object.keys(newErrors).length === 0) {
            try {
                await resetPassword(resetId, resetFormData.password);
                onSuccess();
            } catch (error) {
                console.error('Password reset failed:', error);
                handleAuthError(
                    'Failed to reset password. Please request a new reset link, and try again.',
                );
            }
        } else {
            setErrors(newErrors);
        }
    };

    const validateForm = ({ password, confirmPassword }) => {
        const newErrors = {};
        if (!password) newErrors.password = 'Password is required';
        if (password !== confirmPassword)
            newErrors.confirmPassword = 'Passwords do not match';

        const unmetRequirements = Object.entries(passwordRequirements)
            .filter(([, met]) => !met)
            .map(([req]) => req);

        if (unmetRequirements.length > 0) {
            newErrors.password = `Password does not meet the following character requirements: ${unmetRequirements.join(
                ', ',
            )}`;
        }

        return newErrors;
    };

    const validatePassword = password => {
        setPasswordRequirements({
            length:
                password.length >= companyConfig.application?.password_min_length &&
                password.length <= companyConfig.application?.password_max_length,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            repeats: !/(.)\1{3}/.test(password),
            sequential: !hasSequentialChars(password),
            patterns: !/(.{1,2})\1\1/.test(password),
        });
    };

    const handleChange = event => {
        const { name, value } = event.target;
        const updatedFormData = { ...resetFormData, [name]: value };
        setResetFormData(updatedFormData);

        if (name === 'password') {
            validatePassword(value);
        }

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
        }
    };

    const renderPasswordRequirement = (requirement, text) => {
        const isMet = passwordRequirements[requirement];
        return (
            <small
                className={`password-requirement d-block w-100 ${
                    !resetFormData.password
                        ? 'text-muted'
                        : isMet
                        ? 'text-success'
                        : 'text-danger'
                }`}
            >
                {resetFormData.password && (
                    <i className={`bi ${isMet ? 'bi-check' : 'bi-x'} me-2`}></i>
                )}
                {text}
            </small>
        );
    };

    if (isVerifying) {
        return (
            <div className="text-center my-5">
                <p>{getText('msg_dd3def579810')}</p>
            </div>
        );
    }

    return (
        <form
            className="needs-validation p-2"
            onSubmit={handleSubmit}
            noValidate
        >
            <div
                className={`form-group text-center mx-3 mb-md-5  ${styles.logoContainer}`}
            >
                {themeStyles?.primaryLogo ||
                companyConfig?.public_brand?.logo_url ? (
                    <img
                        src={
                            themeStyles?.primaryLogo ||
                            companyConfig?.public_brand?.logo_url
                        }
                        alt={getText('msg_101d2406160e')}
                    />
                ) : (
                    <PlaceholderLogo />
                )}
            </div>
            <div className="form-instructions my-4">
                <h2 className="iNGAGED-Typography-Heading3 mb-0">
                    {getText('msg_3fb75e3bfe4d')}
                </h2>

                <small htmlFor="password" className="form-label text-muted">
                    {getText('msg_d2ab8d83a56b')}
                </small>
            </div>

            <div className="form-group mb-3">
                <label htmlFor="password">{getText('msg_7cc2398c384f')}</label>
                <PasswordInput
                    id="password"
                    name="password"
                    value={resetFormData.password}
                    onChange={handleChange}
                    placeholder={getText('msg_a8b10891fe35')}
                    errorMessage={errors.password}
                    ref={passwordRef}
                />
                <div className="password-requirements mt-2">
                    {renderPasswordRequirement(
                        'length',
                        `Between ${companyConfig.application?.password_min_length}-${companyConfig.application?.password_max_length} characters`
                    )}
                    {renderPasswordRequirement(
                        'uppercase',
                        'Contains at least 1 uppercase letter',
                    )}
                    {renderPasswordRequirement(
                        'lowercase',
                        'Contains at least 1 lowercase letter',
                    )}
                    {renderPasswordRequirement(
                        'number',
                        'Contains at least 1 number',
                    )}
                    {renderPasswordRequirement(
                        'special',
                        'Contains at least 1 special character',
                    )}
                    {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                        'repeats',
                        'No 4 consecutive repeating characters',
                    )}
                    {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                        'sequential',
                        'No 4 sequential characters',
                    )}
                    {companyConfig.application?.password_allow_duplicate_values || renderPasswordRequirement(
                        'patterns',
                        'No repeating patterns',
                    )}
                </div>
            </div>

            <div className="form-group mb-3">
                <label htmlFor="confirmPassword">{getText('msg_a78da915f5df')}</label>
                <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    value={resetFormData.confirmPassword}
                    onChange={handleChange}
                    placeholder={getText('msg_8b8d6abf91f7')}
                    errorMessage={errors.confirmPassword}
                    isConfirmField={true}
                    originalPassword={resetFormData.password}
                    showMatchFeedback={true}
                />
            </div>

            <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-4">
                <button
                    type="submit"
                    className="ing-button-primary-right mx-auto"
                >
                    {getText('msg_efc007a393f6')}
                </button>
            </div>
        </form>
    );
}

export default ResetPasswordForm;
